/**
 * MyInvitations — Universal Invitation Management Page
 *
 * Available to ALL authenticated roles (strata_admin, property_manager, host, individual).
 * Each role sees a role-appropriate invitation form and manages their own network.
 *
 * Tabs:
 *  - Send Invitation: Email input + role selector (filtered by inviter's role)
 *  - Pending: Sent invitations not yet accepted
 *  - My Network: Users who accepted your invitations (with actions)
 */
import { useState } from "react";
import {
  UserPlus, Mail, Trash2, Clock, Users, Send,
  CheckCircle2, AlertCircle, Building2, RefreshCw,
  MessageSquare, ExternalLink, ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import {
  useSentInvitations,
  useMyInvitees,
  useSendInvitation,
  useRevokeInvitation,
  INVITABLE_ROLES,
  ROLE_LABELS,
} from "@/hooks/useInvitations";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import type { InvitationRole } from "@/types/database";

// ─── Role badge colours ───────────────────────────────────────────────────────
const ROLE_BADGE_CLASS: Record<string, string> = {
  strata_admin:     "bg-violet-100 text-violet-800 border-violet-300",
  property_manager: "bg-blue-100 text-blue-800 border-blue-300",
  host:             "bg-emerald-100 text-emerald-800 border-emerald-300",
  individual:       "bg-amber-100 text-amber-800 border-amber-300",
};

// ─── Avatar initials helper ───────────────────────────────────────────────────
function initials(name?: string) {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

// ─── Time-ago helper ──────────────────────────────────────────────────────────
function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const days  = Math.floor(diff / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30)  return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MyInvitations() {
  const { user } = useAuth();
  const { toast }  = useToast();
  const navigate   = useNavigate();
  const role       = user?.role ?? "host";

  // Available roles this user can invite
  const invitableRoles = INVITABLE_ROLES[role] ?? [];

  // Form state
  const [email, setEmail]         = useState("");
  const [inviteRole, setInviteRole] = useState<InvitationRole>(invitableRoles[0] ?? "individual");
  const [activeTab, setActiveTab] = useState("send");

  // Confirm-revoke dialog
  const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);

  // Data hooks
  const { data: sentInvitations = [], isLoading: loadingSent, refetch: refetchSent }
    = useSentInvitations();
  const { data: invitees = [], isLoading: loadingInvitees, refetch: refetchInvitees }
    = useMyInvitees();

  // Mutations
  const sendInvitation   = useSendInvitation();
  const revokeInvitation = useRevokeInvitation();

  // Split sent into pending vs accepted
  const pendingInvitations  = sentInvitations.filter((inv) => !inv.accepted);
  const expiredInvitations  = pendingInvitations.filter(
    (inv) => new Date(inv.expires_at) < new Date()
  );
  const activePending = pendingInvitations.filter(
    (inv) => new Date(inv.expires_at) >= new Date()
  );

  // ── Send handler ────────────────────────────────────────────────────────────
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Validate role is allowed for this user
    if (!invitableRoles.includes(inviteRole)) {
      toast({
        title:   "Role not allowed",
        description: `You are not permitted to invite users as ${ROLE_LABELS[inviteRole]}.`,
        variant: "destructive",
      });
      return;
    }

    try {
      await sendInvitation.mutateAsync({ email: email.trim(), role: inviteRole });
      toast({
        title:       "Invitation sent! ✉️",
        description: `${email.trim()} will receive an invitation email shortly.`,
      });
      setEmail("");
      setActiveTab("pending");
    } catch (err: any) {
      toast({
        title:       "Failed to send invitation",
        description: err.message,
        variant:     "destructive",
      });
    }
  };

  // ── Revoke handler ──────────────────────────────────────────────────────────
  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await revokeInvitation.mutateAsync(revokeTarget.id);
      toast({ title: "Invitation revoked", description: `Invitation to ${revokeTarget.email} has been cancelled.` });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setRevokeTarget(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <UserPlus className="h-6 w-6 text-primary" />
            My Invitations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Invite people to join Stratakey and monitor your network.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="capitalize text-xs">
            {role.replace("_", " ")}
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => { refetchSent(); refetchInvitees(); }}
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Clock className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending</p>
                <p className="text-xl font-bold">{activePending.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Accepted</p>
                <p className="text-xl font-bold">{invitees.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">My Network</p>
                <p className="text-xl font-bold">{invitees.length + activePending.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Tabs ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="send" className="gap-1.5">
            <Send className="h-3.5 w-3.5" /> Send Invitation
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Pending
            {activePending.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                {activePending.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="network" className="gap-1.5">
            <Users className="h-3.5 w-3.5" /> My Network
            {invitees.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                {invitees.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Send Invitation ── */}
        <TabsContent value="send" className="mt-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Form card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Invite Someone to Stratakey</CardTitle>
                <CardDescription>
                  They'll receive an email with a secure link to create their account.
                  Once accepted, they'll appear in your network.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSend} className="space-y-4">
                  {/* Email */}
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-email">Email address</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>

                  {/* Role selector */}
                  <div className="space-y-1.5">
                    <Label htmlFor="invite-role">Role to assign</Label>
                    <Select
                      value={inviteRole}
                      onValueChange={(v) => setInviteRole(v as InvitationRole)}
                    >
                      <SelectTrigger id="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {invitableRoles.map((r) => (
                          <SelectItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {inviteRole === "strata_admin"
                        ? "Full council-level access to manage the organization."
                        : inviteRole === "property_manager"
                        ? "Operational access to manage hosts, bookings, and compliance."
                        : inviteRole === "host"
                        ? "Access to manage their own STR unit, bookings, and documents."
                        : "Independent property owner — manages their own properties."}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={sendInvitation.isPending || !email}
                  >
                    {sendInvitation.isPending
                      ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                      : <><Send className="mr-2 h-4 w-4" />Send Invitation</>
                    }
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Info card */}
            <div className="space-y-4">
              <Card className="bg-primary/5 border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-primary">How it works</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { step: "1", text: "Enter the email address of the person you want to invite." },
                    { step: "2", text: "Select the role they should have on the platform." },
                    { step: "3", text: "They receive an email with a secure invitation link (valid 7 days)." },
                    { step: "4", text: "They click the link, create or sign into their account, and accept." },
                    { step: "5", text: "They appear in your 'My Network' tab and are linked to your account." },
                  ].map(({ step, text }) => (
                    <div key={step} className="flex items-start gap-3">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground mt-0.5">
                        {step}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Role permissions you can grant */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Roles you can invite</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {invitableRoles.map((r) => (
                    <div key={r} className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${ROLE_BADGE_CLASS[r] ?? "bg-muted text-foreground"}`}>
                        {ROLE_LABELS[r]}
                      </span>
                    </div>
                  ))}
                  {invitableRoles.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Your current role doesn't allow sending invitations.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ── Tab 2: Pending Invitations ── */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Pending Invitations ({activePending.length})
              </CardTitle>
              {expiredInvitations.length > 0 && (
                <p className="text-xs text-amber-600">
                  {expiredInvitations.length} invitation{expiredInvitations.length > 1 ? "s" : ""} expired and not yet accepted.
                </p>
              )}
            </CardHeader>
            <CardContent>
              {loadingSent ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : activePending.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">No pending invitations</p>
                  <p className="text-xs text-muted-foreground max-w-xs">
                    All your invitations have been accepted or there are none yet.
                    Go to the Send tab to invite someone.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab("send")}>
                    <Send className="mr-1.5 h-3.5 w-3.5" /> Send an Invitation
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {activePending.map((inv) => {
                    const daysLeft = Math.ceil(
                      (new Date(inv.expires_at).getTime() - Date.now()) / 86400000
                    );
                    const isExpiringSoon = daysLeft <= 2;
                    return (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between rounded-lg border p-3 gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Avatar */}
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                            <Mail className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{inv.email}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${ROLE_BADGE_CLASS[inv.role] ?? "bg-muted text-foreground border-border"}`}>
                                {ROLE_LABELS[inv.role as InvitationRole] ?? inv.role}
                              </span>
                              {(inv.organization as any)?.name && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <Building2 className="h-2.5 w-2.5" />
                                  {(inv.organization as any).name}
                                </span>
                              )}
                              <span className={`text-[10px] ${isExpiringSoon ? "text-amber-600 font-semibold" : "text-muted-foreground"}`}>
                                {isExpiringSoon
                                  ? `⚠ Expires in ${daysLeft}d`
                                  : `Expires in ${daysLeft}d`}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10"
                          onClick={() => setRevokeTarget({ id: inv.id, email: inv.email })}
                          title="Revoke invitation"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Expired section */}
              {expiredInvitations.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Expired ({expiredInvitations.length})
                  </p>
                  <div className="space-y-2 opacity-60">
                    {expiredInvitations.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between rounded-lg border border-dashed p-3 gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
                            <AlertCircle className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{inv.email}</p>
                            <p className="text-[10px] text-muted-foreground">
                              Expired {new Date(inv.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10"
                          onClick={() => setRevokeTarget({ id: inv.id, email: inv.email })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: My Network ── */}
        <TabsContent value="network" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                My Network ({invitees.length})
              </CardTitle>
              <CardDescription className="text-xs">
                People who accepted your invitations. You can message or monitor them from here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingInvitees ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : invitees.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                    <Users className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium">No one has accepted yet</p>
                  <p className="text-xs text-muted-foreground max-w-xs leading-relaxed">
                    Once someone accepts your invitation and registers, they'll appear here.
                    You'll be notified when they join.
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab("send")}>
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Send an Invitation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {invitees.map((invitee) => {
                    const p = invitee.profile;
                    return (
                      <div
                        key={invitee.invitationId}
                        className="flex items-center justify-between rounded-lg border p-3 gap-3 hover:bg-muted/40 transition-colors"
                      >
                        {/* Left: avatar + info */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                            {initials(p?.full_name)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">
                              {p?.full_name ?? "—"}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {p?.email ?? invitee.profile?.email ?? "—"}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${ROLE_BADGE_CLASS[invitee.invitedRole] ?? "bg-muted text-foreground border-border"}`}>
                                {ROLE_LABELS[invitee.invitedRole] ?? invitee.invitedRole}
                              </span>
                              {(invitee.organization as any)?.name && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <Building2 className="h-2.5 w-2.5" />
                                  {(invitee.organization as any).name}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground">
                                Joined {timeAgo(invitee.invitedAt)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right: action buttons */}
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            title="Send message"
                            onClick={() => navigate("/messages")}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </Button>
                          {/* Role-specific deep-link */}
                          {(invitee.invitedRole === "host" || invitee.invitedRole === "individual") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              title="View hosts"
                              onClick={() => navigate("/hosts")}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {invitee.invitedRole === "property_manager" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              title="View team"
                              onClick={() => navigate("/team")}
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Revoke confirmation dialog ── */}
      <Dialog
        open={!!revokeTarget}
        onOpenChange={(open) => { if (!open) setRevokeTarget(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel the invitation sent to{" "}
              <strong>{revokeTarget?.email}</strong>? The invitation link will no longer work.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeInvitation.isPending}
              onClick={handleRevoke}
            >
              {revokeInvitation.isPending ? "Revoking…" : "Revoke Invitation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
